import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExportButton, type ExportButtonProps } from './ExportButton';
import { CalculatorPageContext } from './calculatorPageContext';

/** Element in the app header (see layout/Header.tsx) that hosts the page action bar. */
export const HEADER_ACTIONS_SLOT_ID = 'header-page-actions';
/** Below this width the header has no room, so the action bar renders in the page. */
const INLINE_ACTIONS_QUERY = '(max-width: 768px)';

/**
 * The header slot to portal the action bar into, or null when the bar should
 * render inline (phones, or no header mounted — e.g. a page rendered alone in
 * a test).
 */
function useHeaderActionsSlot(): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(INLINE_ACTIONS_QUERY) : null;
    const update = () => setSlot(mq?.matches ? null : document.getElementById(HEADER_ACTIONS_SLOT_ID));
    update();
    mq?.addEventListener('change', update);
    return () => mq?.removeEventListener('change', update);
  }, []);
  return slot;
}

export interface CalculatorPageProps {
  /** Page slug, also applied as a class for page-scoped styles ("wire-ampacity"). */
  id: string;
  title: string;
  /** One sentence: what the page sizes and which NEC articles govern it (screen readers only). */
  description: string;
  /** Renders the Reset button in the action bar. */
  onReset?: () => void;
  /** Renders the Export menu in the action bar. */
  exportProps?: ExportButtonProps;
  /** Presets menu or mode switch; rendered first in the action bar. */
  presets?: ReactNode;
  /** Any other page-level controls, rendered before Reset. */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Shared page shell for every calculator.
 *
 * The title and description are visually hidden (screen readers only): the app
 * header already names the page. On phones the header drops its title and the
 * page title shows instead. The action bar (mode switch / presets, Reset, Export) is portaled
 * into the app header so it stays reachable while the page scrolls; on phones
 * it renders at the top of the page instead.
 *
 *   <CalculatorPage id="wire-ampacity" title="Wire Ampacity" description="…" onReset={…} exportProps={…}>
 *     <AnswerStrip … />
 *     <div className="calculator-grid">
 *       <div className="calculator-inputs card">…</div>
 *       <div className="calculator-outputs card">…</div>
 *     </div>
 *     <DetailsBand>…</DetailsBand>
 *   </CalculatorPage>
 */
export function CalculatorPage({
  id,
  title,
  description,
  onReset,
  exportProps,
  presets,
  actions,
  children,
}: CalculatorPageProps) {
  const hasActions = presets || actions || onReset || exportProps;
  const slot = useHeaderActionsSlot();
  // The page id on the host keeps page-scoped selectors (".generator .calc-actions …")
  // working when the bar lives in the header.
  const actionBar = hasActions ? (
    <div className={`calc-actions-host ${id}`}>
      <div className="calc-actions">
        {presets}
        {actions}
        {onReset && (
          <button type="button" className="btn-reset" onClick={onReset}>
            Reset
          </button>
        )}
        {exportProps && <ExportButton {...exportProps} />}
      </div>
    </div>
  ) : null;
  return (
    <CalculatorPageContext.Provider value={{ id }}>
      <div className={`calculator calc-page ${id}`}>
        <h2 className="calc-title">{title}</h2>
        <p className="calc-description">{description}</p>
        {actionBar && (slot ? createPortal(actionBar, slot) : <div className="calc-actions-inline">{actionBar}</div>)}
        {children}
      </div>
    </CalculatorPageContext.Provider>
  );
}

export default CalculatorPage;
